import { PRN_STATUS, type PrnShiftPosting, type PrnStatus } from "../types";

interface DerivePrnStatusParams {
  posting: PrnShiftPosting;
}

// Derives the chip state for a PRN shift posting on the active-postings
// list. Single seam between the FE chip layer and the BE list-endpoint
// shape — `useActivePrnShiftPostings` has already adapted the BE response
// into `PrnShiftPosting` flat shape before calling this.
//
// Chip state machine — only the four states reachable from the
// active-postings list:
//
//   booked (any mode)                          → BOOKED_PRN
//   active + "immediately"                     → ROSTER_OPEN (the up-front
//                                                  marketplace shift is the
//                                                  user-visible state; this
//                                                  branch only fires for
//                                                  the 1-hour overlap before
//                                                  BE filters the group)
//   active + "on_expiry"                       → PRN_PRIORITY ("Roster
//                                                  priority" — fall through
//                                                  to Clipboard at expiry)
//   active + "never"                           → PRN_ONLY ("Roster only" —
//                                                  no Clipboard fall-through)
//
// Note: `ROSTER_OPEN` for on_expiry-mode postings post-expiry and
// `PRN_ONLY_UNCLAIMED` for never-mode postings post-shift-start are NOT
// reached here. Once the BE expiration job fires, all sibling invites
// flip to EXPIRED and the BE drops the group from the list (its
// `activeCount > 0` filter). Window-mode auto-released shifts then surface
// via the day-shifts response with `postingSource: "prn_roster"` (handled
// by `derivePrnStatusFromShift`); never-mode expired postings have nothing
// to render once they fall off the list.
//
// Defensive `undefined` branch: BE (after ALCH-6096 / #24849) always
// returns `postToMarketplace` via `requiredEnumWithFallback`, so this
// branch isn't reached in practice. Kept as a safe default — Window mode
// is the dominant case, and "Roster priority" is the most informative
// neutral chip if anything ever changes wire-side.
export function derivePrnStatus(params: DerivePrnStatusParams): PrnStatus {
  const { posting } = params;

  if (posting.status === "BOOKED") {
    return PRN_STATUS.BOOKED_PRN;
  }

  switch (posting.postToMarketplace) {
    case "immediately": {
      return PRN_STATUS.ROSTER_OPEN;
    }
    case "never": {
      return PRN_STATUS.PRN_ONLY;
    }
    case "on_expiry":
    case undefined:
    default: {
      return PRN_STATUS.PRN_PRIORITY;
    }
  }
}

interface DerivePrnStatusFromShiftParams {
  /**
   * `postingSource` from the BE day-shifts response. `"prn_roster"`
   * indicates the Shift was created from a PRN posting (immediate-mode
   * up-front or on-expiry auto-released, per ALCH-6004 / ALCH-6006).
   * Anything else is a regular Clipboard shift.
   */
  postingSource: string | undefined;
  /**
   * True when the Shift has been claimed by some worker. For booked
   * Roster-originated shifts we currently return `undefined` (the regular
   * booked chip applies). The "booked by a roster worker → BOOKED_PRN"
   * path needs the M4 claim flow to exist before it's worth wiring.
   */
  isBooked: boolean;
  /**
   * True when the shift's start time has passed. For unbooked
   * Roster-originated shifts, the chip flips from `ROSTER_OPEN` (priority
   * / marketplace window still live) to `ROSTER_OFFERED_UNCLAIMED` (the
   * PRN lifecycle has run its course and the shift is now visibly
   * unclaimed). NEVER-mode shifts have their own `PRN_ONLY_UNCLAIMED`
   * state surfaced by the BE; this branch is the IMMEDIATELY / on_expiry
   * mode counterpart (ALCH-6131).
   */
  isShiftStartPast: boolean;
  /**
   * True when the BE's active-PRN-postings list still includes a group
   * whose `shiftId` points at this shift — i.e. there is at least one
   * PENDING-not-expired or ACCEPTED sibling PRN invite.
   *
   * When a Clipboard marketplace worker books and then cancels a
   * PRN-originated shift, the BE flips all sibling PRN invites to
   * DELETED at book time and never revives them on cancel (ALCH-6178).
   * Without this signal, the chip would still light up `ROSTER_OPEN` /
   * `ROSTER_OFFERED_UNCLAIMED` after the cancel even though no roster
   * worker can actually claim the shift. Pass `false` to suppress those
   * states and render the shift as a plain marketplace shift.
   */
  hasActiveInvites: boolean;
}

// Derives the chip state for a real `Shift` document on the day-shifts
// response. Single-purpose companion to `derivePrnStatus` (which handles
// the active-postings list path) — used for shifts that originated from a
// PRN posting and now appear as marketplace `Shift` documents.
//
//   postingSource !== "prn_roster"                     → undefined
//   postingSource === "prn_roster" + booked            → undefined
//                                                         (regular booked
//                                                         state takes over;
//                                                         M4 will refine to
//                                                         BOOKED_PRN once
//                                                         the roster-claim
//                                                         path exists)
//   postingSource === "prn_roster" + open + !invites   → undefined
//                                                         (Clipboard cancel
//                                                         after invites were
//                                                         closed; ALCH-6178)
//   postingSource === "prn_roster" + open + pre-start  → ROSTER_OPEN
//   postingSource === "prn_roster" + open + post-start → ROSTER_OFFERED_UNCLAIMED
export function derivePrnStatusFromShift(
  params: DerivePrnStatusFromShiftParams
): PrnStatus | undefined {
  const { postingSource, isBooked, isShiftStartPast, hasActiveInvites } = params;
  if (postingSource !== "prn_roster") {
    return undefined;
  }
  if (isBooked) {
    return undefined;
  }
  if (!hasActiveInvites) {
    return undefined;
  }
  if (isShiftStartPast) {
    return PRN_STATUS.ROSTER_OFFERED_UNCLAIMED;
  }
  return PRN_STATUS.ROSTER_OPEN;
}
